import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// Same base-URL derivation as api.ts, minus the '/api' suffix — Socket.IO
// connects to the bare origin and negotiates its own '/socket.io' path.
const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';
const SOCKET_URL = API_BASE_URL.replace(/\/api\/?$/, '') || undefined;

let socket: Socket | null = null;
let socketToken: string | null = null;

// Re-creates the connection if the stored token changed since it was opened
// (e.g. a different user logged in in the same tab without a full reload).
function ensureSocket(): Socket {
  const token = localStorage.getItem('authToken');
  if (socket && socketToken === token) {
    return socket;
  }
  socket?.disconnect();
  socketToken = token;
  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
  });
  return socket;
}

export interface PendingApprovalsCount {
  total: number;
  grnCount: number;
  dispatchCount: number;
  pastGrnCount: number;
  pastDispatchCount: number;
}

/**
 * Drives the navbar notification bell: counts GRN/Dispatch/past-update items
 * awaiting THIS user's approval (permission-gated, mirroring InventoryPage's
 * Pending Approvals tab), and re-checks instantly over a WebSocket push
 * whenever anyone submits/approves/rejects one — so an item that someone
 * else just actioned disappears from this user's bell without waiting on a
 * poll interval. The socket only ever carries a lightweight "something
 * changed" signal; the REST count queries stay the single source of truth.
 */
export function usePendingApprovalsBell() {
  const queryClient = useQueryClient();
  const { user, hasPermission, isAuthenticated } = useAuth();

  const canApproveGrn = hasPermission('grn:approve');
  const canApproveDispatch = hasPermission('dispatch:approve');
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes(
    (user?.role?.name || '').toUpperCase(),
  );
  const canSeeBell = isAuthenticated && (canApproveGrn || canApproveDispatch || isAdmin);

  useEffect(() => {
    if (!canSeeBell) return;

    const s = ensureSocket();
    const handleChange = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'pending-approvals-count'] });
    };

    s.on('pending-approvals:changed', handleChange);
    s.on('connect_error', () => {
      // Silent — the 60s poll fallback still keeps counts fresh even if the
      // socket can't connect (e.g. a proxy blocking WebSocket upgrade).
    });
    if (!s.connected) s.connect();

    return () => {
      s.off('pending-approvals:changed', handleChange);
    };
  }, [canSeeBell, queryClient, user?.id]);

  const { data } = useQuery<PendingApprovalsCount>({
    queryKey: ['notifications', 'pending-approvals-count'],
    queryFn: async () => {
      const [grnRes, dispatchRes, pastGrnRes, pastDispatchRes] = await Promise.all([
        canApproveGrn
          ? api.get('/grn', { params: { status: 'submitted', page: 1, limit: 1 } })
          : Promise.resolve(null),
        canApproveDispatch
          ? api.get('/dispatch', { params: { status: 'submitted', page: 1, limit: 1 } })
          : Promise.resolve(null),
        isAdmin
          ? api.get('/grn/past-updates', { params: { status: 'pending' } })
          : Promise.resolve(null),
        isAdmin
          ? api.get('/dispatch/past-updates', { params: { status: 'pending' } })
          : Promise.resolve(null),
      ]);

      const grnCount = grnRes?.data?.meta?.total ?? 0;
      const dispatchCount = dispatchRes?.data?.meta?.total ?? 0;
      const pastGrnCount = Array.isArray(pastGrnRes?.data) ? pastGrnRes!.data.length : 0;
      const pastDispatchCount = Array.isArray(pastDispatchRes?.data) ? pastDispatchRes!.data.length : 0;

      return {
        total: grnCount + dispatchCount + pastGrnCount + pastDispatchCount,
        grnCount,
        dispatchCount,
        pastGrnCount,
        pastDispatchCount,
      };
    },
    enabled: canSeeBell,
    refetchInterval: 60000,
    staleTime: 0,
  });

  return {
    canSeeBell,
    total: data?.total ?? 0,
    breakdown: data,
  };
}
